import React from 'react';
import {AgeGroup} from '@guestapp/sdk';
import {AGE_USER_TYPE, COUNTRY_CODES} from 'utils/constants';
import {NAVIGATION_STATUSES, useModalControls} from 'hooks';
import {POLICE_TYPE_CODE, SHOW_MODAL_AFTER_SPLASH_TIMEOUT} from './constants';
import {useSummary} from 'context/summary';
import {useReservation} from 'context/reservation';
import {WhoComingModalSheet} from './WhoComingModalSheet';

const getSpainAge = (policeType?: string) => {
  if (!policeType) {
    return null;
  } else if (policeType === 'ERT' || policeType === 'POL') {
    return 14;
  } else if (POLICE_TYPE_CODE.includes(policeType)) {
    return 16;
  } else {
    return null;
  }
};

type UseWhoIsComingModalProps = {
  showFirstTime?: boolean;
  openModalPerSession?: boolean;
};

function useWhoIsComingModal({
  openModalPerSession,
  showFirstTime,
}: UseWhoIsComingModalProps = {}) {
  const {data} = useSummary();
  const {housing} = useReservation();
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>();
  const {
    isOpen: isOpenConfirmModal,
    closeModal: closeConfirmModal,
    openModal: openConfirmModal,
  } = useModalControls();
  const yearsAbove = data?.booking?.age_group.find(
    (ag: AgeGroup) => ag?.name === AGE_USER_TYPE.adult,
  );

  const countryCode = data?.country_code;
  const isActiveTaxes = data?.taxes?.active;

  const validateGuestAge = React.useMemo(() => {
    if (isActiveTaxes || !countryCode) {
      return {ageLimit: null};
    }

    const ageLimitByCountry = {
      [COUNTRY_CODES.spain]: {ageLimit: getSpainAge(housing?.police_account_type)},
      [COUNTRY_CODES.france]: {ageLimit: 15},
      [COUNTRY_CODES.uk]: {ageLimit: 16},
      [COUNTRY_CODES.greece]: {ageLimit: 14},
      [COUNTRY_CODES.sweden]: {ageLimit: 18},
    }[countryCode];

    return ageLimitByCountry || {ageLimit: null};
  }, [countryCode, isActiveTaxes, housing]);

  React.useEffect(() => {
    if (showFirstTime) {
      const isProcessComplete =
        data?.guest_registration?.status === NAVIGATION_STATUSES.complete;
      if (openModalPerSession && !isProcessComplete) {
        timeoutRef.current = setTimeout(() => {
          openConfirmModal();
          return;
        }, SHOW_MODAL_AFTER_SPLASH_TIMEOUT);
      }
    }
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [
    openModalPerSession,
    openConfirmModal,
    data?.guest_registration?.status,
    showFirstTime,
  ]);

  const modal = React.useCallback(
    () => (
      <WhoComingModalSheet
        isOpen={isOpenConfirmModal}
        onClose={closeConfirmModal}
        yearsAbove={yearsAbove?.base_age}
        ageLimit={validateGuestAge.ageLimit}
      />
    ),
    [isOpenConfirmModal, closeConfirmModal, yearsAbove?.base_age, validateGuestAge],
  );

  return {
    isOpen: isOpenConfirmModal,
    closeModal: closeConfirmModal,
    openModal: openConfirmModal,
    modal,
    ageLimit: validateGuestAge.ageLimit,
  };
}

export {useWhoIsComingModal};
